const MAX_SAMPLES = 100;
const NATIVE_SAMPLE_INTERVAL_MS = 5000;

/**
 * CMCD metrics accumulator.
 * Collects buffer length (bl), bitrate (br), and measured throughput (mtp) samples
 * from hls.js CMCD query parameters or native HLS buffer sampling.
 */
const cmcdMetrics = {
	samples: [],

	record(parsed) {
		const sample = {};
		if (parsed.bl !== undefined) sample.bl = Number(parsed.bl);
		if (parsed.br !== undefined) sample.br = Number(parsed.br);
		if (parsed.mtp !== undefined) sample.mtp = Number(parsed.mtp);
		if (parsed.ot !== undefined) sample.ot = String(parsed.ot);
		if (parsed.bs !== undefined) sample.bs = parsed.bs === true || parsed.bs === 'true' || parsed.bs === '';
		if (parsed.su !== undefined) sample.su = parsed.su === true || parsed.su === 'true' || parsed.su === '';
		if (Object.keys(sample).length > 0) {
			this.samples.push(sample);
			if (this.samples.length > MAX_SAMPLES) {
				this.samples.splice(0, this.samples.length - MAX_SAMPLES);
			}
		}
	},

	getAverages() {
		if (this.samples.length === 0) return null;
		const sums = { bl: 0, br: 0, mtp: 0 };
		const counts = { bl: 0, br: 0, mtp: 0 };
		let bsCount = 0;
		let suCount = 0;
		let brSwitchCount = 0;
		let previousBr = null;
		let headroomSum = 0;
		let headroomCount = 0;
		let audioRequestCount = 0;
		let videoRequestCount = 0;
		for (const sample of this.samples) {
			for (const key of ['bl', 'br', 'mtp']) {
				if (sample[key] !== undefined && !isNaN(sample[key])) {
					sums[key] += sample[key];
					counts[key]++;
				}
			}

			if (sample.bs) {
				bsCount++;
			}
			if (sample.su) {
				suCount++;
			}
			if (sample.ot === 'a') {
				audioRequestCount++;
			}
			if (sample.ot === 'v') {
				videoRequestCount++;
			}

			if (!isNaN(sample.br) && sample.br > 0) {
				if (previousBr !== null && previousBr !== sample.br) {
					brSwitchCount++;
				}
				previousBr = sample.br;
			}

			if (!isNaN(sample.mtp) && sample.mtp > 0 && !isNaN(sample.br) && sample.br > 0) {
				headroomSum += sample.mtp / sample.br;
				headroomCount++;
			}
		}
		const averages = {};
		if (counts.bl > 0) averages.avgBufferLength = Math.round(sums.bl / counts.bl);
		if (counts.br > 0) averages.avgBitrate = Math.round(sums.br / counts.br);
		if (counts.mtp > 0) averages.avgThroughput = Math.round(sums.mtp / counts.mtp);
		averages.bsCount = bsCount;
		averages.suCount = suCount;
		averages.brSwitchCount = brSwitchCount;
		if (headroomCount > 0) averages.headroomAvg = Math.round((headroomSum / headroomCount) * 100) / 100;
		averages.audioWithoutVideoCount = videoRequestCount === 0 ? audioRequestCount : 0;
		return Object.keys(averages).length > 0 ? averages : null;
	},
};

/**
 * Parse CMCD query parameters from a request URL and record them.
 */
export function recordCmcdFromUrl(url) {
	try {
		const parsed = new URL(url, window.location.origin);
		const cmcd = parsed.searchParams.get('CMCD');
		if (cmcd) {
			const data = Object.fromEntries(
				cmcd.split(',').map(pair => {
					const [key, ...rest] = pair.split('=');
					const value = rest.join('=');
					return [key, rest.length > 0 ? value.replace(/^"|"$/g, '') : true];
				}),
			);
			cmcdMetrics.record(data);
		}
	} catch (e) {
		// ignore parse errors
	}
}

/**
 * Sample buffer length from a native video element (for Safari/iOS native HLS).
 * Returns an interval ID that must be cleared on cleanup.
 */
export function startNativeBufferSampler(videoEl) {
	return setInterval(() => {
		if (!videoEl || videoEl.paused || !videoEl.buffered.length) return;
		const currentTime = videoEl.currentTime;
		let bufferEnd = 0;
		const bufferedRanges = videoEl.buffered;
		for (let rangeIndex = 0; rangeIndex < bufferedRanges.length; rangeIndex++) {
			let rangeStart;
			let rangeEnd;
			try {
				rangeStart = bufferedRanges.start(rangeIndex);
				rangeEnd = bufferedRanges.end(rangeIndex);
			} catch {
				// Some TV browsers mutate TimeRanges while sampling; skip this tick instead of breaking playback telemetry.
				return;
			}

			if (rangeStart <= currentTime && rangeEnd >= currentTime) {
				bufferEnd = rangeEnd;
				break;
			}
		}
		const bl = Math.round((bufferEnd - currentTime) * 1000);
		if (bl >= 0) {
			cmcdMetrics.record({ bl: String(bl) });
		}
	}, NATIVE_SAMPLE_INTERVAL_MS);
}

/**
 * Get CMCD averages for heartbeat telemetry.
 */
export function getCmcdAverages() {
	return cmcdMetrics.getAverages();
}
